import {
  ArchiveDocumentsBoxBig16,
  Check,
  ChevronLeft,
  IconFormIcons,
  Target,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  IconButton,
  Sizes as IconButtonSizes,
} from 'shared/components/ui/icon-button';
import {
  PopupMenuButton,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { Colors } from 'shared/components/ui/popup-menu/types';
import { ID } from 'shared/types/id';
import { useTheme } from 'styled-components';

import { GoalTreeItem, GoalTreeItemIds } from '../types';
import * as Styled from './move-goal-item.style';

type MoveGoalItemProps = {
  item: GoalTreeItem;
  isSelected: boolean;
  isDisabled: boolean;
  isExpanded: boolean;
  searchValue: string;
  onGoal: (parentIds: ID[], lifeAreaId?: ID | null) => void;
  toggleCollapse: (e: React.MouseEvent, id: ID) => void;
  isParent: boolean;
};

export const MoveGoalItem = ({
  item,
  isSelected,
  isDisabled,
  isExpanded,
  onGoal,
  toggleCollapse,
  searchValue,
  isParent,
}: MoveGoalItemProps) => {
  const theme = useTheme();

  if (item.type === GoalTreeItemIds.NoLifeArea) {
    const onNoLifeArea = () => {
      if (isDisabled) {
        return;
      }
      onGoal([], null);
    };

    return (
      <PopupMenuListItem key={GoalTreeItemIds.NoLifeArea}>
        <PopupMenuButton
          onClick={onNoLifeArea}
          isSelected={isSelected}
          indentationLevel={0}
          textColor={Colors.Dark}
          end={
            !searchValue && item.hasChildren ? (
              <IconButton
                icon={ChevronLeft}
                size={IconButtonSizes.Small}
                // If Expanded: -90 (Down v). If Collapsed: 0 (Left <).
                rotation={isExpanded ? -90 : 0}
                noHover
                onClick={(e) => toggleCollapse(e, GoalTreeItemIds.NoLifeArea)}
              />
            ) : undefined
          }
        >
          {item.name}
        </PopupMenuButton>
      </PopupMenuListItem>
    );
  }

  if (item.type === GoalTreeItemIds.LifeArea) {
    const onLifeArea = () => {
      if (isDisabled) {
        return;
      }
      onGoal([], item.id);
    };

    return (
      <PopupMenuListItem key={`life-area-${item.id}`}>
        <PopupMenuButton
          onClick={onLifeArea}
          isSelected={isSelected}
          start={
            <Icon
              icon={IconFormIcons[item.iconName]}
              style={{ color: item.color ?? undefined }}
            />
          }
          indentationLevel={0}
          disabled={isDisabled}
          end={
            !searchValue && item.hasChildren ? (
              <IconButton
                icon={ChevronLeft}
                size={IconButtonSizes.Small}
                // If Expanded: -90 (Down v). If Collapsed: 0 (Left <).
                rotation={isExpanded ? -90 : 0}
                noHover
                onClick={(e) => toggleCollapse(e, item.id)}
              />
            ) : undefined
          }
        >
          {item.name}
        </PopupMenuButton>
      </PopupMenuListItem>
    );
  }

  const onItem = () => {
    if (isDisabled) {
      return;
    }
    onGoal([...(item.parentIds ?? []), item.id], item.lifeAreaId);
  };

  const itemStyle = isParent
    ? {
        backgroundColor:
          theme.palette.primaryExtended.opacityBackgroundBackground,
        color: theme.palette.text.primary,
        opacity: 1,
      }
    : isDisabled
      ? { opacity: 0.4, pointerEvents: 'none' as const }
      : undefined;

  return (
    <PopupMenuListItem key={`goal-${item.id}`}>
      <PopupMenuButton
        onClick={onItem}
        isSelected={isParent}
        style={itemStyle}
        start={
          <Styled.IconWrapper>
            <Icon
              icon={
                item.archivedAt
                  ? ArchiveDocumentsBoxBig16
                  : item.iconName
                    ? IconFormIcons[item.iconName]
                    : Target
              }
              style={{
                color: item.archivedAt
                  ? theme.palette.grey[600]
                  : item.displayColor ?? undefined,
              }}
            />
            {!item.archivedAt && !!item.completedAt && (
              <Styled.StatusBadge>
                <Icon icon={Check} />
              </Styled.StatusBadge>
            )}
          </Styled.IconWrapper>
        }
        indentationLevel={item.indentationLevel}
        disabled={isDisabled}
        end={
          !searchValue && item.hasChildren ? (
            <IconButton
              icon={ChevronLeft}
              size={IconButtonSizes.Small}
              // If Expanded: -90 (Down v). If Collapsed: 0 (Left <).
              rotation={isExpanded ? -90 : 0}
              noHover
              onClick={(e) => toggleCollapse(e, item.id)}
            />
          ) : undefined
        }
      >
        <Styled.GoalInfoContainer>
          {!!item.parentName && (
            <Styled.ParentLabel>{item.parentName}</Styled.ParentLabel>
          )}
          <Styled.GoalName
            $isCompleted={!!item.completedAt}
            $isArchived={!!item.archivedAt}
          >
            {item.name}
          </Styled.GoalName>
        </Styled.GoalInfoContainer>
      </PopupMenuButton>
    </PopupMenuListItem>
  );
};
